
import { Link } from 'react-router-dom';

export default function HeinrichsLawPage() {
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 text-white py-32 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-black/20 via-black/10 to-black/20"></div>
        <div className="max-w-5xl mx-auto px-6 relative z-10">
          <div className="inline-block bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full text-sm font-medium mb-6">
            ハインリッヒの法則
          </div>
          <h1 className="text-5xl md:text-6xl font-bold mb-6 leading-tight">
            インシデントが多い薬局ほど<br />「安全」になり得る理由
          </h1>
          <p className="text-2xl text-white/90 font-medium">
            報告は減らすものではなく、育てるもの
          </p>
        </div>
      </section>

      {/* Conclusion */}
      <section className="py-16 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="bg-gradient-to-r from-orange-500 to-amber-500 text-white p-8 rounded-2xl shadow-lg">
            <h2 className="text-2xl font-bold mb-4 flex items-center gap-3">
              <i className="ri-lightbulb-flash-line text-3xl"></i>
              結論
            </h2>
            <p className="text-lg leading-relaxed">
              インシデント報告が多い薬局は、必ずしも危険ではありません。むしろ、<strong>学習が回り始めている薬局</strong>である可能性が高い。
            </p>
          </div>
        </div>
      </section>

      {/* Theory Explanation */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold mb-12 text-gray-900">理論の解説｜ハインリッヒの法則とは何か</h2>
          
          <div className="prose prose-lg max-w-none mb-12">
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              ハインリッヒの法則は、労働安全の研究者 <strong>ハーバート・ハインリッヒ</strong>が提唱した「安全三角形」の考え方です。
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              有名な表現はこれです。
            </p>
          </div>

          {/* Heinrich's Triangle */}
          <div className="bg-gradient-to-br from-red-50 to-orange-50 p-10 rounded-2xl mb-12">
            <div className="flex flex-col items-center gap-6">
              <div className="w-full max-w-md">
                {/* Top - 1 Major Accident */}
                <div className="bg-gradient-to-r from-red-600 to-red-700 text-white p-6 rounded-t-2xl text-center">
                  <div className="text-4xl font-bold mb-2">1</div>
                  <div className="text-lg font-medium">重大事故</div>
                </div>
                
                {/* Middle - 29 Minor Accidents */}
                <div className="bg-gradient-to-r from-orange-500 to-orange-600 text-white p-6 text-center">
                  <div className="text-4xl font-bold mb-2">29</div>
                  <div className="text-lg font-medium">軽微事故</div>
                </div>
                
                {/* Bottom - 300 Near Misses */}
                <div className="bg-gradient-to-r from-amber-400 to-yellow-500 text-white p-6 rounded-b-2xl text-center">
                  <div className="text-4xl font-bold mb-2">300</div>
                  <div className="text-lg font-medium">ヒヤリハット</div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-red-50 border-l-4 border-red-500 p-6 rounded-r-xl mb-12">
            <p className="text-lg text-gray-800 leading-relaxed">
              <strong>ただし、ここで最も重要な注意点があります。</strong>
            </p>
            <p className="text-lg text-gray-800 leading-relaxed mt-4">
              👉 この「1：29：300」は<strong>固定比率ではありません</strong>。領域・業務・文化によって大きく変動します。
            </p>
            <p className="text-lg text-gray-800 leading-relaxed mt-4">
              つまり、<strong>数字を当てにして予測する理論ではない</strong>のです。
            </p>
          </div>
        </div>
      </section>

      {/* True Usage */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-amber-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold mb-12 text-gray-900">ハインリッヒの法則の「本当の使い方」</h2>
          
          <div className="prose prose-lg max-w-none mb-8">
            <p className="text-lg text-gray-700 leading-relaxed">
              この法則の本質は、比率ではありません。
            </p>
          </div>

          <div className="bg-white p-8 rounded-2xl shadow-lg border-2 border-orange-200">
            <p className="text-xl text-gray-900 leading-relaxed font-medium">
              重大事故を防ぐ唯一の方法は、ピラミッドの底辺（ヒヤリハット）を<strong className="text-orange-600">可視化</strong>し、<strong className="text-orange-600">学習</strong>に変えること
            </p>
          </div>

          <div className="mt-8 flex gap-4 justify-center">
            <div className="bg-orange-100 px-6 py-3 rounded-full">
              <span className="text-lg font-bold text-orange-700">可視化</span>
            </div>
            <div className="bg-amber-100 px-6 py-3 rounded-full">
              <span className="text-lg font-bold text-amber-700">学習</span>
            </div>
          </div>
        </div>
      </section>

      {/* Wrong Interpretation */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold mb-12 text-gray-900">調剤薬局で起きやすい"誤った解釈"</h2>
          
          <div className="prose prose-lg max-w-none mb-8">
            <p className="text-lg text-gray-700 leading-relaxed">
              多くの薬局で、こんな反応が起きます。
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-red-50 p-6 rounded-xl border-2 border-red-200">
              <div className="flex items-start gap-3 mb-3">
                <i className="ri-close-circle-line text-2xl text-red-600 mt-1"></i>
                <div className="text-lg font-bold text-red-700">誤った解釈</div>
              </div>
              <div className="space-y-3">
                <p className="text-gray-800">インシデントが増えた → 「最近、危なくなっている」</p>
                <p className="text-gray-800">報告が多い店舗 → 「管理が甘い」</p>
              </div>
            </div>

            <div className="bg-green-50 p-6 rounded-xl border-2 border-green-200">
              <div className="flex items-start gap-3 mb-3">
                <i className="ri-checkbox-circle-line text-2xl text-green-600 mt-1"></i>
                <div className="text-lg font-bold text-green-700">正しい読み替え</div>
              </div>
              <div className="space-y-3">
                <p className="text-gray-800">報告が増えた → <strong>検出感度が上がった</strong></p>
                <p className="text-gray-800">何も報告が出ない → <strong>見えていないだけ</strong></p>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-r from-red-500 to-orange-500 text-white p-6 rounded-xl">
            <p className="text-xl font-bold text-center">
              👉 沈黙している薬局ほど、リスクが高い。
            </p>
          </div>
        </div>
      </section>

      {/* Redefinition */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold mb-12 text-gray-900">調剤薬局への翻訳｜インシデントの再定義</h2>
          
          <div className="prose prose-lg max-w-none mb-8">
            <p className="text-lg text-gray-700 leading-relaxed">
              まず定義を変えます。
            </p>
          </div>

          <div className="space-y-6">
            <div className="bg-red-50 p-6 rounded-xl border-l-4 border-red-500">
              <div className="flex items-center gap-3 mb-2">
                <i className="ri-close-line text-2xl text-red-600"></i>
                <span className="text-lg font-bold text-red-700">間違った定義</span>
              </div>
              <p className="text-xl text-gray-800">
                インシデント ＝ ミスの証拠
              </p>
            </div>

            <div className="bg-green-50 p-6 rounded-xl border-l-4 border-green-500">
              <div className="flex items-center gap-3 mb-2">
                <i className="ri-check-line text-2xl text-green-600"></i>
                <span className="text-lg font-bold text-green-700">正しい定義</span>
              </div>
              <p className="text-xl text-gray-800 font-medium">
                インシデント ＝ <strong className="text-green-700">システム欠陥の検出装置</strong>
              </p>
            </div>
          </div>

          <div className="mt-8 bg-white p-6 rounded-xl shadow-lg">
            <p className="text-lg text-gray-800 leading-relaxed">
              この再定義がない限り、<strong>報告文化は絶対に育ちません</strong>。
            </p>
          </div>
        </div>
      </section>

      {/* Case Study */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold mb-12 text-gray-900">具体例｜報告が増えたのに事故が減る薬局</h2>
          
          <div className="space-y-8">
            {/* Step 1 */}
            <div className="bg-gradient-to-r from-blue-50 to-cyan-50 p-8 rounded-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold">1</div>
                <h3 className="text-2xl font-bold text-gray-900">施策</h3>
              </div>
              <ul className="space-y-2 text-lg text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-blue-500 mt-1"></i>
                  <span>投薬時の復唱を標準化</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-blue-500 mt-1"></i>
                  <span>「違和感があったら言っていい」と明言</span>
                </li>
              </ul>
            </div>

            {/* Step 2 */}
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 p-8 rounded-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-amber-500 text-white rounded-full flex items-center justify-center font-bold">2</div>
                <h3 className="text-2xl font-bold text-gray-900">起きたこと</h3>
              </div>
              <ul className="space-y-2 text-lg text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-amber-500 mt-1"></i>
                  <span>患者・家族からの「あれ？」という声が増える</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-amber-500 mt-1"></i>
                  <span>ヒヤリハット件数は増加</span>
                </li>
              </ul>
            </div>

            {/* Step 3 */}
            <div className="bg-gradient-to-r from-green-50 to-emerald-50 p-8 rounded-2xl">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 bg-green-500 text-white rounded-full flex items-center justify-center font-bold">3</div>
                <h3 className="text-2xl font-bold text-gray-900">結果</h3>
              </div>
              <ul className="space-y-2 text-lg text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-green-500 mt-1"></i>
                  <span><strong>誤薬は投薬前で止まる</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-checkbox-circle-line text-green-500 mt-1"></i>
                  <span><strong>重大事故はむしろ減少</strong></span>
                </li>
              </ul>
            </div>
          </div>

          <div className="mt-8 bg-gradient-to-r from-green-500 to-emerald-500 text-white p-6 rounded-xl">
            <p className="text-xl font-bold text-center">
              👉 これは学習→是正→再発防止の循環が回っている状態です。
            </p>
          </div>
        </div>
      </section>

      {/* Operation Framework */}
      <section className="py-20 bg-gradient-to-br from-orange-50 to-red-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold mb-12 text-gray-900">ハインリッヒを"資産"に変える運用の型</h2>
          
          <div className="prose prose-lg max-w-none mb-8">
            <p className="text-lg text-gray-700 leading-relaxed">
              インシデントを報告させるだけでは不十分です。「<strong>回路</strong>」にする必要があります。
            </p>
          </div>

          <div className="space-y-6">
            {/* Step 1 */}
            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-blue-500">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-blue-500 text-white rounded-full flex items-center justify-center font-bold text-lg">1</div>
                <h3 className="text-xl font-bold text-gray-900">報告（集める）</h3>
              </div>
              <p className="text-gray-700">簡単・短時間・責めない</p>
            </div>

            {/* Step 2 */}
            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-indigo-500">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-indigo-500 text-white rounded-full flex items-center justify-center font-bold text-lg">2</div>
                <h3 className="text-xl font-bold text-gray-900">分類（意味づけ）</h3>
              </div>
              <div className="flex flex-wrap gap-2 mt-3">
                <span className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-sm">用法用量</span>
                <span className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-sm">薬剤選択</span>
                <span className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-sm">規格</span>
                <span className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-sm">患者取り違え</span>
              </div>
            </div>

            {/* Step 3 */}
            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-purple-500">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-purple-500 text-white rounded-full flex items-center justify-center font-bold text-lg">3</div>
                <h3 className="text-xl font-bold text-gray-900">是正（動かす）</h3>
              </div>
              <ul className="space-y-1 text-gray-700">
                <li>• 仕組み変更</li>
                <li>• ルール修正</li>
                <li>• 教育の一点集中</li>
              </ul>
            </div>

            {/* Step 4 */}
            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-pink-500">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-pink-500 text-white rounded-full flex items-center justify-center font-bold text-lg">4</div>
                <h3 className="text-xl font-bold text-gray-900">完了（閉じる）</h3>
              </div>
              <p className="text-gray-700">「やった」で終わらせない<br />完了日を必ず決める</p>
            </div>

            {/* Step 5 */}
            <div className="bg-white p-6 rounded-xl shadow-lg border-l-4 border-red-500">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 bg-red-500 text-white rounded-full flex items-center justify-center font-bold text-lg">5</div>
                <h3 className="text-xl font-bold text-gray-900">再発監視（学ぶ）</h3>
              </div>
              <p className="text-gray-700">同カテゴリが再出現していないか</p>
            </div>
          </div>
        </div>
      </section>

      {/* KPI */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold mb-12 text-gray-900">KPI｜学習する組織を測る指標</h2>
          
          <div className="prose prose-lg max-w-none mb-8">
            <p className="text-lg text-gray-700 leading-relaxed">
              事故件数だけを見てはいけません。
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              見るべきは、次の4つです。
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-12">
            <div className="bg-gradient-to-br from-orange-50 to-amber-50 p-6 rounded-xl border-2 border-orange-200">
              <div className="w-12 h-12 bg-orange-500 text-white rounded-xl flex items-center justify-center mb-4">
                <i className="ri-file-list-3-line text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">ヒヤリハット報告密度</h3>
              <p className="text-gray-700">例：1000枚あたり◯件</p>
            </div>

            <div className="bg-gradient-to-br from-green-50 to-emerald-50 p-6 rounded-xl border-2 border-green-200">
              <div className="w-12 h-12 bg-green-500 text-white rounded-xl flex items-center justify-center mb-4">
                <i className="ri-checkbox-circle-line text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">是正完了率</h3>
              <p className="text-gray-700">報告→対策→完了</p>
            </div>

            <div className="bg-gradient-to-br from-red-50 to-pink-50 p-6 rounded-xl border-2 border-red-200">
              <div className="w-12 h-12 bg-red-500 text-white rounded-xl flex items-center justify-center mb-4">
                <i className="ri-repeat-line text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">再発率</h3>
              <p className="text-gray-700">同カテゴリの再出現</p>
            </div>

            <div className="bg-gradient-to-br from-blue-50 to-cyan-50 p-6 rounded-xl border-2 border-blue-200">
              <div className="w-12 h-12 bg-blue-500 text-white rounded-xl flex items-center justify-center mb-4">
                <i className="ri-shield-check-line text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">上流で止めた率</h3>
              <p className="text-gray-700">監査・鑑査で止めた割合</p>
            </div>
          </div>

          <div className="bg-gradient-to-r from-orange-500 to-red-500 text-white p-6 rounded-xl">
            <p className="text-xl font-bold text-center">
              👉 報告数×是正完了率が高い薬局ほど、安全。
            </p>
          </div>
        </div>
      </section>

      {/* Safety Culture */}
      <section className="py-20 bg-gradient-to-br from-amber-50 to-orange-50">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold mb-12 text-gray-900">安全文化は「数」ではなく「扱い方」で決まる</h2>
          
          <div className="prose prose-lg max-w-none mb-8">
            <p className="text-lg text-gray-700 leading-relaxed">
              同じインシデント数でも、
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {/* Bad Culture */}
            <div className="bg-white p-8 rounded-2xl shadow-lg border-2 border-red-200">
              <div className="flex items-center gap-3 mb-6">
                <i className="ri-close-circle-line text-3xl text-red-600"></i>
                <h3 className="text-2xl font-bold text-red-700">報告が枯れる薬局</h3>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-red-500 mt-1"></i>
                  <span>責められる</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-red-500 mt-1"></i>
                  <span>放置される</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-red-500 mt-1"></i>
                  <span>形だけで終わる</span>
                </li>
              </ul>
            </div>

            {/* Good Culture */}
            <div className="bg-white p-8 rounded-2xl shadow-lg border-2 border-green-200">
              <div className="flex items-center gap-3 mb-6">
                <i className="ri-checkbox-circle-line text-3xl text-green-600"></i>
                <h3 className="text-2xl font-bold text-green-700">安全が自己増殖する薬局</h3>
              </div>
              <ul className="space-y-3 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-green-500 mt-1"></i>
                  <span><strong>ありがとうと言われる</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-green-500 mt-1"></i>
                  <span><strong>対策が見える</strong></span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-arrow-right-s-line text-green-500 mt-1"></i>
                  <span><strong>次に活かされる</strong></span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Tomorrow's Action */}
      <section className="py-20 bg-gradient-to-br from-orange-500 via-red-500 to-pink-500 text-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-4xl font-bold mb-12">明日の一手｜最初に変えるべき1点</h2>
          
          <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border-2 border-white/30 mb-8">
            <p className="text-xl mb-4">明日やることは、これだけです。</p>
            <p className="text-3xl font-bold">
              👉 「報告してくれてありがとう」を必ず口に出す。
            </p>
          </div>

          <div className="bg-white/10 backdrop-blur-sm p-8 rounded-2xl border-2 border-white/30">
            <p className="text-xl mb-4">それだけで、</p>
            <div className="grid md:grid-cols-3 gap-4">
              <div className="bg-white/20 backdrop-blur-sm p-4 rounded-xl text-center">
                <div className="text-2xl font-bold mb-2">報告量</div>
                <i className="ri-arrow-up-line text-3xl"></i>
              </div>
              <div className="bg-white/20 backdrop-blur-sm p-4 rounded-xl text-center">
                <div className="text-2xl font-bold mb-2">情報の質</div>
                <i className="ri-arrow-up-line text-3xl"></i>
              </div>
              <div className="bg-white/20 backdrop-blur-sm p-4 rounded-xl text-center">
                <div className="text-2xl font-bold mb-2">学習速度</div>
                <i className="ri-arrow-up-line text-3xl"></i>
              </div>
            </div>
            <p className="text-xl mt-6 text-center font-bold">が確実に変わります。</p>
          </div>
        </div>
      </section>

      {/* Related Links */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold mb-8 text-gray-900">関連する理論</h2>
          <div className="grid md:grid-cols-3 gap-6">
            <Link 
              to="/theory/swiss-cheese-model" 
              className="bg-gradient-to-br from-red-50 to-pink-50 p-6 rounded-xl border-2 border-red-200 hover:border-red-400 transition-all hover:shadow-lg group"
            >
              <div className="w-12 h-12 bg-red-500 text-white rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-shield-cross-line text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">スイスチーズ・モデル</h3>
              <p className="text-sm text-gray-600">多重防御の構造設計</p>
            </Link>

            <Link 
              to="/theory/system-thinking" 
              className="bg-gradient-to-br from-purple-50 to-indigo-50 p-6 rounded-xl border-2 border-purple-200 hover:border-purple-400 transition-all hover:shadow-lg group"
            >
              <div className="w-12 h-12 bg-purple-500 text-white rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-links-line text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">システム思考</h3>
              <p className="text-sm text-gray-600">全体最適の視点</p>
            </Link>

            <Link 
              to="/theory" 
              className="bg-gradient-to-br from-gray-50 to-slate-50 p-6 rounded-xl border-2 border-gray-200 hover:border-gray-400 transition-all hover:shadow-lg group"
            >
              <div className="w-12 h-12 bg-gray-700 text-white rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                <i className="ri-book-open-line text-2xl"></i>
              </div>
              <h3 className="text-lg font-bold text-gray-900 mb-2">理論一覧</h3>
              <p className="text-sm text-gray-600">すべての理論を見る</p>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
